/**
 * Diagnostic scan orchestration: drive the ELM327 through init → identity →
 * fault codes → readiness → live data, decoding each reply with the shared
 * obd2 library, and assemble a serializable ObdScanResult.
 */

import {
  CMD_PENDING_DTCS,
  CMD_READINESS,
  CMD_STORED_DTCS,
  CMD_VIN,
  DEFAULT_SCAN_PIDS,
  ELM327_INIT_SEQUENCE,
  buildPidCommand,
  cleanElmResponse,
  decodeDtcResponse,
  decodeVinResponse,
  enrichDtc,
  isNoData,
  parsePidResponse,
  parseReadinessMonitors,
  type Dtc,
  type ObdScanResult,
  type PidReading,
  type ReadinessReport,
} from '@tendedloop/shared';
import type { Elm327Transport } from './transport';

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface ScanProgress {
  phase: 'init' | 'identity' | 'codes' | 'readiness' | 'live' | 'done';
  /** 0..1 */
  fraction: number;
  label: string;
}

export interface RunScanOptions {
  onProgress?: (p: ScanProgress) => void;
  /** Subset of live PIDs to read; defaults to DEFAULT_SCAN_PIDS. */
  pids?: typeof DEFAULT_SCAN_PIDS;
}

/**
 * Run a full diagnostic scan. Each phase is best-effort: a NO DATA / timeout on
 * one PID or service never aborts the whole scan — we collect what the ECU
 * gives us. Returns a serializable result ready to POST to the API.
 */
export async function runDiagnosticScan(
  transport: Elm327Transport,
  options: RunScanOptions = {},
): Promise<ObdScanResult> {
  const { onProgress } = options;
  const pids = options.pids ?? DEFAULT_SCAN_PIDS;
  const startedAt = Date.now();

  // --- init ---
  onProgress?.({ phase: 'init', fraction: 0.05, label: 'init' });
  for (const step of ELM327_INIT_SEQUENCE) {
    try {
      await transport.sendCommand(step.cmd, 6000);
    } catch {
      /* ATZ sometimes returns garbage as the chip resets; continue */
    }
    await delay(step.delayMs);
  }

  // --- identity (VIN) ---
  onProgress?.({ phase: 'identity', fraction: 0.15, label: 'identity' });
  let vin: string | undefined;
  try {
    const raw = await transport.sendCommand(CMD_VIN, 6000);
    if (!isNoData(raw)) vin = decodeVinResponse(cleanElmResponse(raw)) ?? undefined;
  } catch {
    /* VIN is optional */
  }

  // --- fault codes ---
  onProgress?.({ phase: 'codes', fraction: 0.3, label: 'codes' });
  const storedDtcs = await readDtcs(transport, CMD_STORED_DTCS, 'stored');
  const pendingDtcs = await readDtcs(transport, CMD_PENDING_DTCS, 'pending');

  // --- readiness ---
  onProgress?.({ phase: 'readiness', fraction: 0.45, label: 'readiness' });
  let readiness: ReadinessReport | undefined;
  try {
    const raw = await transport.sendCommand(CMD_READINESS, 5000);
    if (!isNoData(raw)) readiness = parseReadinessMonitors(cleanElmResponse(raw)) ?? undefined;
  } catch {
    /* readiness optional */
  }

  // --- live PIDs ---
  const pidReadings: PidReading[] = [];
  for (let i = 0; i < pids.length; i++) {
    const pid = pids[i];
    if (!pid) continue;
    onProgress?.({
      phase: 'live',
      fraction: 0.5 + 0.5 * ((i + 1) / pids.length),
      label: 'live',
    });
    try {
      const raw = await transport.sendCommand(buildPidCommand(pid), 4000);
      if (isNoData(raw)) continue;
      const reading = parsePidResponse(pid, cleanElmResponse(raw));
      if (reading) pidReadings.push(reading);
    } catch {
      /* skip unsupported / timed-out PID */
    }
  }

  onProgress?.({ phase: 'done', fraction: 1, label: 'done' });
  return {
    scannedAt: new Date().toISOString(),
    vin,
    storedDtcs,
    pendingDtcs,
    readiness,
    pids: pidReadings,
    adapterName: transport.deviceName,
    durationMs: Date.now() - startedAt,
  };
}

async function readDtcs(transport: Elm327Transport, cmd: string, status: 'stored' | 'pending'): Promise<Dtc[]> {
  try {
    const raw = await transport.sendCommand(cmd, 6000);
    if (isNoData(raw)) return [];
    return decodeDtcResponse(cleanElmResponse(raw), status).map(enrichDtc);
  } catch {
    return [];
  }
}
